import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ExcelStyle, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { STATE_ACTIVE_FILTERS, STATE_CUSTOMER_IN_URL, STATE_TABLE_UPDATING, } from "../constants/InvoicesRecoil";
import { formatCurrency, formatDate, formatDateWithoutTime } from "../../../utils/FormatUtils";
import { useInvoiceRepository } from "../../../api/repositories/InvoiceRepository";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import InvoiceTableActions, { InvoicePageType } from "../components/InvoiceTableActions";
import FinanceDialog from "../../../component/FinanceDialog";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import {showStatusColumn} from "./InvoiceTableUtil";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import { ID_CREDIT_NOTE, ID_DEBIT_NOTE, ID_INVOICE } from "../constants/InvoicesTypes";
import { NumericSummary } from "../../../component/NumericSummary";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { useLocation } from "react-router-dom";
import { useAgGridLocale } from "../../../../../config/globalConfig";
import { FileDownloadRounded } from "@mui/icons-material";
import Button from "@mui/material/Button";
import dayjs from "dayjs";
import { convertTypology, statuses } from "../constants/InvoicesStatuses";
import {ExportDataAsExcelBase} from "../../../utils/ExportDataAsExcelBase";

const InvoicesTable = (props: any) => {

    const UNIQUE_ID = "fnn-invoices";
    
    const { t } = useTranslation("finance", { keyPrefix: "invoices" });
    const { getInvoices, deleteInvoice, getSummary, reprocessInvoice, reissueInvoice } = useInvoiceRepository();

    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);
    const [customerInUrl, setCustomerValue] = useRecoilState(STATE_CUSTOMER_IN_URL);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const customerId = queryParams.get('customerId');
    if (customerId) {
        setCustomerValue(customerId)
      } else {
        setCustomerValue(null)
      }
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);
    const gridRef = useRef<AgGridReact>(null);
    const agGridLocale = useAgGridLocale();

    const [warningOpen, setWarningOpen] = useState(false);
    const [warningTitle, setWarningTitle] = useState("");
    const [openActionDialog, setOpenActionDialog] = useState(false);
    const [actionDialogId, setActionDialogId] = useState<string>('');
    const [actionDialogType, setActionDialogType] = useState<string>('');
    
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");
    

    function paymentMethods (paymentMethod: string) {
        switch (paymentMethod) {
            case "J": {
                return  t("paymentMethods.CREDITCARD");
            }
            case "D": {
                return t("paymentMethods.DEBITCARD")
            }
            case "B": {
                return  t("paymentMethods.BANKTRANSFER")
            }
            case "P": {
                return t("paymentMethods.AMAZON")
            }
            case "A": {
                return t("paymentMethods.AMEXSOF")
            }
        }
    }


    const columnDefs : ColDef[] = [
        {
            field: "invoiceNumber", headerName: t("tables.invoice.id-proforma"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true,

        },
        {
            field: "sapInvoiceNumber", headerName: t("tables.invoice.id-invoice"),
            minWidth: 200,
            valueFormatter: (params) => params.value || '---',
            unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true, cellClass:"typeString"
        },
        {
            field: "parent.sapInvoiceNumber", headerName: t("tables.invoice.id-invoice-ref"),
            minWidth: 200,
            valueFormatter: (params) => params.value || '---',
            unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true, cellClass:"typeString"
        },
        {
            field: "invoiceDate", headerName: t("tables.invoice.invoice-date"),
            minWidth: 160,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "dueDate", headerName: t("tables.invoice.due-date"),
            minWidth: 170,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "customer.sapId", headerName: t("tables.invoice.id-customer"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "customer.companyName", headerName: t("tables.invoice.customer-company-name"),
            valueFormatter: (params) => params.value || '---',
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "customer.lastName", headerName: t("tables.invoice.customer"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            valueFormatter: (params) => params.value ? `${params.data.customer.firstName} ${params.value} ` : '---',
        },
        {
            field: "documentType", headerName: t("tables.invoice.type"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            valueFormatter: params => t(`types.${params.value}`)
        },
        {
            field: "createdDate", headerName: t("tables.invoice.created-date"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => formatDate(params.value),
        },
        {
            field: "invoiceSchema.description", headerName: t("tables.invoice.payment-terms"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "totalNetPrice", headerName: t("tables.invoice.net-amount"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            valueFormatter: (params) => formatCurrency(params.value, params.data.currency, 0.01),
        },
        {
            field: "totalVat", headerName: t("tables.invoice.vat-amount"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            valueFormatter: (params) => formatCurrency(params.value, params.data.currency, 0.01),
        },
        {
            field: "totalGrossPrice", headerName: t("tables.invoice.total-amount"),
            minWidth: 190,
            unSortIcon: true, sortable: true,
            valueFormatter: (params) => formatCurrency(params.value, params.data.currency, 0.01),
        },

        {
            field: "paymentMethod.code", headerName: t("tables.invoice.payment-method"),
            minWidth: 220,
            unSortIcon: true, sortable: true,
            valueFormatter: (params) => {
                let paymentMethod: string = params.data.paymentMethod.code;
                return paymentMethods(paymentMethod);
            },
        },
        {
            field: "status", headerName: t("tables.invoice.status"),
            minWidth: 150,
            unSortIcon: true, sortable: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                return showStatusColumn(status);
            },
        },
        {
            field: "legalEntity", headerName: t("tables.invoice.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.invoice.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => (<InvoiceTableActions page={InvoicePageType.INVOICE} id={params.data.id}
                        invoiceId={params.data.invoiceNumber}
                        invoiceType={params.data.documentType}
                        invoiceStatus={params.data.status}
                        paymentMethod={params.data.paymentMethod.code}
                        setWarningOpen={setWarningOpen}
                        setWarningTitle={setWarningTitle}
                        setOpenActionDialog={setOpenActionDialog}
                        setActionDialogId={setActionDialogId}
                        setActionDialogType={setActionDialogType}
                    />),
                };
            },
            suppressFiltersToolPanel: true,
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,

        },
        noRowsOverlayComponentParams: { title: t('tables.invoice.no-rows-overlay.title') }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort;

                getInvoices(startRow, endRow - startRow, sort, order, filterDefs, customerId)
                    .then((response) => {
                        let result = response.result;

                        if (result.page.totalElements === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }
                        params.success({
                            rowData: result.content,
                            rowCount: result.page.totalElements,
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
            <NumericSummary query={() => getSummary(filterDefs)} fields={[ID_INVOICE, ID_DEBIT_NOTE, ID_CREDIT_NOTE]}
                i18nPath={"invoices.counters"} />
        ),
        renderActions: () => (
          <>
            <Button fullWidth color={"accent"} variant="contained" component="label" onClick={exportAsExcel}>
              <FileDownloadRounded /> {t('exportInvoice')}
            </Button>
          </>
        )
    }

    function getExportableExcelColumns(): string[] {
        let keys: string[] = gridRef?.current?.columnApi.getAllDisplayedColumns().map(column => column.getColId()) as string[];
        let amountIndex: number = keys.findIndex(column => column === 'status');
    
        return keys;
      }
    
      function exportAsExcel() {
        gridRef?.current?.api.exportDataAsExcel({
          ...ExportDataAsExcelBase,
            author: "Finance - Invoice",
          fileName: `Invoice - ${dayjs().format("L")}.xlsx`,
          sheetName: `Invoice`,
          columnKeys: getExportableExcelColumns(),
          processCellCallback: (params) => {
            if (params.column.getColId() === 'totalGrossPrice' || params.column.getColId() === 'totalNetPrice' || params.column.getColId() === 'totalVat') {
                if (params.value) {
                    const numericValue = parseFloat(params.value) / 100;
                    return numericValue.toFixed(2) + " €";
                  } else {
                    return "";
                  }
              }
              if (params.column.getColId() === 'status') {
                return t(convertTypology(statuses, params.value));
              }
              if (params.column.getColId() === 'createdDate') {
                return formatDate(params.value);
              }
              if (params.column.getColId() === 'customer.lastName') {
                return params.value ? `${params.value} ${params.node?.data?.customer.firstName}` : '---'
              }
              if (params.column.getColId() === 'dueDate' || params.column.getColId() === 'invoiceDate') {
                return formatDateWithoutTime(params.value);
              }
              if (params.column.getColId() === 'documentType') {
                return t(`types.${params.value}`);
              }
              return params.value;
          }
        });
      }

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    function reissue(actionDialogId: any) {
        return reissueInvoice(actionDialogId).then((response) => {
            if (response.status === "KO" && response.code === 11){
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("snackbar." + response.message),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            } else {
                setUpdating(true);
            }
        });
    }

    function getTitle(actionDialogType: any){
        if (actionDialogType === 'reissue') {
            return t('action-dialog.reissue.title');
        } else if (actionDialogType === 'reprocess') {
            return t('action-dialog.reprocess.title');
        } else if (actionDialogType === 'reprocess-unaccounted') {
            return t('action-dialog.reprocess-unaccounted.title');
        } else return "";
    }

    function getContent(actionDialogType: any){
        if (actionDialogType === 'reissue') {
            return t('action-dialog.reissue.text');
        } else if (actionDialogType === 'reprocess') {
            return t('action-dialog.reprocess.text');
        } else if (actionDialogType === 'reprocess-unaccounted') {
            return t('action-dialog.reprocess-unaccounted.text');
        } else return "";
    }

    const excelStyles: ExcelStyle[] = [
        {
          id: "typeString",
          dataType: "String"
        }
      ];

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                rowSelection={"multiple"}
                excelStyles={excelStyles}
                suppressRowClickSelection
                noRowsOverlayComponent={noRowsOverlayComponent}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true
                        })
                    }
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    setLocalGridState(UNIQUE_ID, {
                        rows: gridRef.current!.api?.paginationGetPageSize(),
                        cols: getLocalGridState(UNIQUE_ID).cols
                    });
                }}
            ></AgGridCustomTable>
            <FinanceDialog open={warningOpen}
                onClose={() => setWarningOpen(false)}
                callback={() => deleteInvoice(warningTitle).then(() => setUpdating(true))}
                titleIcon={<InfoRoundedIcon sx={{ color: 'accent.main' }} />}
                title={t('delete-confirmation.title')}
                content={t('delete-confirmation.text', { invoiceId: warningTitle })}
                confirmButton={t('delete-confirmation.confirm')}
                cancelButton={t('delete-confirmation.cancel')}
            />
            <FinanceDialog open={openActionDialog}
                onClose={() => setOpenActionDialog(false)}
                callback={actionDialogType === 'reissue' ? () => reissue(actionDialogId) : () => reprocessInvoice(actionDialogId).then(() => setUpdating(true))}
                titleIcon={<InfoRoundedIcon sx={{ color: 'accent.main' }} />}
                title={getTitle(actionDialogType)}
                content={getContent(actionDialogType)}
                confirmButton={t('action-dialog.confirm')}
                cancelButton={t('action-dialog.cancel')}
            />
        </>
    );
};




export default InvoicesTable;
